import type { ParameterDimensionDefinitionInOut } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty } from '@festina-life/core'
import { useCallback } from 'react'

import { createLiteralTypeInformation } from '../../../components/fplusExpression/expressionType'
import { FPlusExpressionEditor } from '../../../components/fplusExpression/FPlusExpressionEditor'
import { FPlusExpressionViewer } from '../../../components/fplusExpression/FPlusExpressionViewer'
import type { FPlusExpressionProps } from '../../../components/fplusExpression/types'
import { useParameterContext } from '../../../context/parameterContext'
import type { FPlusExpressionValue } from '../../../types/templateModel'
import { useUpdateCurrentParameterDefinitionDimension } from '../../../uiModels/parameters/useUpdateCurrentParameterDefinitionDimension'
import {
  type ParameterDimensionType,
  ParameterDimensionTypeToLiteralExpressionType,
} from './types'

type Props = {
  data: ParameterDimensionDefinitionInOut
  isTransparent: boolean
}

export const ParameterDimensionExpression = ({
  data,
  isTransparent,
}: Props) => {
  const { parameterDefinitionOperation } = useParameterContext()
  const updateCurrentParameterDimension =
    useUpdateCurrentParameterDefinitionDimension()

  assertNotEmpty(parameterDefinitionOperation)

  const { isDraft } = parameterDefinitionOperation

  const literalType =
    ParameterDimensionTypeToLiteralExpressionType[
      data.dimensionType as ParameterDimensionType
    ]
  const expressionProps: FPlusExpressionProps = {
    typeInformation: data.dimensionType
      ? createLiteralTypeInformation({
          literalType,
          source: data.dimensionExpression,
        })
      : undefined,
    isTransparent,
    expression: data.dimensionExpression
      ? { fplusSource: data.dimensionExpression }
      : null,
    path: data.name ?? '',
    shouldShowOverflowFallback: false,
    type: 'parameterDefinition',
  }

  const handleExpressionChanged = useCallback(
    (nextValue: FPlusExpressionValue) => {
      assertNotEmpty(updateCurrentParameterDimension)
      const nextDimension = {
        ...data,
        dimensionExpression: nextValue.fplusSource,
      } satisfies ParameterDimensionDefinitionInOut

      return updateCurrentParameterDimension?.(nextDimension)
    },
    [data, updateCurrentParameterDimension],
  )

  return isDraft ? (
    <FPlusExpressionEditor
      onChange={handleExpressionChanged}
      fallbackCode={expressionProps.expression?.fplusSource ?? ''}
      {...expressionProps}
    />
  ) : (
    <FPlusExpressionViewer
      alignment="center"
      isShowingFunctionDeclaration={false}
      {...expressionProps}
    />
  )
}
